// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FunnelChart
 * @package @geenius/analytics/react
 * @description Renders the React funnel visualization for a shared funnel
 * analysis result. The component presents each step's conversion bar plus the
 * overall conversion summary for onboarding and checkout flows.
 */

import type { FunnelResult } from "@geenius/analytics";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Progress,
} from "@geenius/ui/react";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

/**
 * Props for rendering the React funnel chart.
 *
 * @property result Funnel analysis result to visualize.
 * @property error Optional funnel-level error message or object.
 * @property onRetry Optional retry handler rendered when an error is present.
 * @property className Optional class name merged into the chart wrapper.
 */
export interface FunnelChartProps {
  result: FunnelResult;
  error?: Error | string | null;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders the React funnel chart for a shared funnel result.
 *
 * @param props Funnel result payload for the chart.
 * @returns A funnel chart element, or an empty-state element when no steps exist.
 */
export function FunnelChart(props: FunnelChartProps): React.ReactElement {
  const copy = useAnalyticsCopy();

  if (props.error) {
    return (
      <Card className={props.className}>
        <CardContent className="pt-6">
          <div className="grid gap-3" role="alert">
            <span className="text-sm font-medium text-foreground">
              {props.result?.name ??
                copy.t("analytics.dashboard.conversionRate")}
            </span>
            <span className="text-sm text-muted-foreground">
              {getErrorMessage(props.error)}
            </span>
            {props.onRetry && (
              <Button
                className="w-fit"
                type="button"
                variant="secondary"
                onClick={props.onRetry}
              >
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!props.result || props.result.steps.length === 0) {
    return (
      <Card className={props.className}>
        <CardContent className="pt-6">
          <EmptyState
            bordered={false}
            compact
            title={copy.t("analytics.funnel.empty")}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={props.className}>
      <CardHeader className="pb-0">
        <CardTitle className="text-sm" data-analytics="funnel-title">
          {props.result.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 pt-4">
        <div className="flex flex-col gap-3" data-analytics="funnel-steps">
          {props.result.steps.map((step, index) => (
            <div
              key={index}
              className="grid gap-2"
              data-analytics="funnel-step"
            >
              <div className="flex items-center justify-between gap-3">
                <span
                  className="text-sm font-medium text-foreground"
                  data-analytics="funnel-step-name"
                >
                  {step.name}
                </span>
                <Badge variant="secondary" data-analytics="funnel-step-count">
                  {step.count}
                </Badge>
              </div>
              <Progress
                data-analytics="funnel-bar"
                value={step.conversionRate}
              />
              {index > 0 && (
                <span
                  className="text-xs text-muted-foreground"
                  data-analytics="funnel-dropoff"
                >
                  {copy.t("analytics.funnel.dropoff", {
                    percentage: step.dropoffRate.toFixed(1),
                  })}
                </span>
              )}
            </div>
          ))}
        </div>
        <div
          className="flex items-center justify-between gap-2 text-sm text-muted-foreground"
          data-analytics="funnel-summary"
        >
          <span>
            {copy.t("analytics.funnel.overall", {
              percentage: props.result.overallConversionRate.toFixed(1),
            })}
          </span>
          <span>
            {props.result.totalConversions}/{props.result.totalEntries}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
